﻿//==========================================================================================================
// Copyright (C) Nore Technology AB. All Rights Reserved.
//==========================================================================================================

// A component for the content section of a splash page.

import { applyThemeStyles } from "~base/Styling";
import classnames from "classnames";
import * as React from "react";
import { createUseStyles } from "react-jss";
import { JssClass } from "~base/Styling";
import { ConsilioBaseTheme } from "~base/Styling/ConsilioTheme";
import { RouteLink } from "~base/Components/Primitives/Link";
import { Icon } from "~base/Components/Primitives/Icon";

// ----------------------------------------------------------------------------------------------------
// Props
// ----------------------------------------------------------------------------------------------------

interface SplashContentProps {
    closeRouteUri?: string;
    close?: React.ReactNode;
    header: React.ReactNode;
    children: React.ReactNode;
    footer: React.ReactNode;
    constWidth?: boolean;
    onCloseIconFQN?: string;
}

//-------------------------------------------------------------------------------------
// Styling
//-------------------------------------------------------------------------------------

export interface ConsilioThemePagesSharedSplashContentStyles {
    separator?: JssClass;
    headerSeparator?: JssClass;
    allContent?: JssClass;
    allContentConstWidth?: JssClass;
    headerContent?: JssClass;
    mainContent?: JssClass;
    footerContent?: JssClass;
    closeButton?: JssClass;
    closeIcon?: JssClass;
}

const useStyles = createUseStyles(
    (theme: ConsilioBaseTheme) => ({
        allContent: applyThemeStyles(
            theme.components.pages.shared.splashContent.allContent,
            {
                position: "relative",
                background: "#FFF",
                maxWidth: "340px",
            }
        ),

        allContentConstWidth: applyThemeStyles(
            theme.components.pages.shared.splashContent.allContentConstWidth,
            {
                width: "340px",
            }
        ),

        separator: applyThemeStyles(
            theme.components.pages.shared.splashContent.separator,
            {
                margin: "20px 36px",
                height: "1px",
                background: "#e1e1e1",
                border: "none",
            }
        ),

        headerSeparator: applyThemeStyles(
            theme.components.pages.shared.splashContent.headerSeparator,
            {
                /*display: "none",*/
            }
        ),
        headerContent: applyThemeStyles(
            theme.components.pages.shared.splashContent.headerContent,
            {
                paddingTop: "1px",
            }
        ),

        mainContent: applyThemeStyles(
            theme.components.pages.shared.splashContent.mainContent,
            {
                background: "#FFF",
                padding: "0px 25px 0px 25px",
            }
        ),

        footerContent: applyThemeStyles(
            theme.components.pages.shared.splashContent.footerContent,
            {
                background: "#F7F7F7",
                padding: "25px",
            }
        ),
        closeButton: applyThemeStyles(
            theme.components.pages.shared.splashContent.closeButton,
            {
                top: 0,
                right: 0,
                position: "absolute",
                padding: "8px",
                borderTopRightRadius: "5px",
                borderBottomLeftRadius: "5px",
                backgroundColor: "#F7F7F7",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                boxSizing: "border-box",
                marginRight: "8px",
                height: "34px",
                width: "34px",
            }
        ),

        closeIcon: applyThemeStyles(
            theme.components.pages.shared.splashContent.closeIcon,
            {
                height: "30px",
                width: "30px",
            }
        ),
    }),
    { name: "SplashContent" }
);

// ----------------------------------------------------------------------------------------------------
// Component
// ----------------------------------------------------------------------------------------------------

const SplashContent: React.FunctionComponent<SplashContentProps> = ({
    header,
    children,
    footer,
    closeRouteUri,
    close,
    constWidth = false,
    onCloseIconFQN,
}) => {
    const classes = useStyles();
    return (
        <div
            className={classnames(
                classes.allContent,
                constWidth ? classes.allContentConstWidth : null
            )}
        >
            <div className={classes.headerContent}>{header}</div>
            <div className={classes.headerSeparator}>
                <hr className={classes.separator} />
            </div>
            <div className={classes.mainContent}>{children}</div>

            <div className={classes.footerContent}>{footer}</div>

            {closeRouteUri && (
                <RouteLink className={classes.closeButton} to={closeRouteUri}>
                    <Icon
                        iconFQN={onCloseIconFQN}
                        svgClassName={classes.closeIcon}
                    />
                </RouteLink>
            )}
            {close && <div className={classes.closeButton}>{close}</div>}
        </div>
    );
};

export default SplashContent;
export { SplashContent };
